/**
 * RBAC Context - Role-Based Access Control
 *
 * Provides role and permission checking throughout the application
 * Manages user claims from Firebase Auth tokens
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth } from '../firebase/config';
import { User } from 'firebase/auth';
import {
  UserClaims,
  UserRole,
  ResourceAction,
  RoleDefinition,
  AdminUser,
  isAdmin,
  isSuperAdmin,
  canManageRole as canManageRoleUtil,
} from '../types/rbac';
import { DEFAULT_ROLE_DEFINITIONS } from '../types/rbacRoleDefinitions';
import AdminUsersApiService from '../services/adminUsersApi';
import { logger } from '../lib/logger';

// ============================================================================
// CONTEXT TYPE
// ============================================================================

interface RBACContextType {
  claims: UserClaims | null;
  userProfile: AdminUser | null;
  loading: boolean;
  error: string | null;

  // Role checks
  hasRole: (role: UserRole | UserRole[]) => boolean;
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
  canManageRole: (targetRole: UserRole) => boolean;

  // Permission checks
  hasPermission: (resource: string, action: string | ResourceAction) => boolean;
  canAccessClass: (classId: string) => boolean;
  canUseAICorrection: () => boolean;

  // Utility methods
  getAccessibleClassIds: () => string[];
  refreshClaims: () => Promise<void>;
  getRoleDefinitions: () => RoleDefinition[];
}

// ============================================================================
// CONTEXT CREATION
// ============================================================================

const RBACContext = createContext<RBACContextType | undefined>(undefined);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

interface RBACProviderProps {
  children: React.ReactNode;
}

/** Message d'un compte connecté sans rôle back-office. */
export const NO_ROLE_ERROR = 'Aucun rôle back-office attribué à ce compte';

/** Vrai si la valeur est un rôle back-office connu. */
const isUserRole = (value: unknown): value is UserRole =>
  typeof value === 'string' && (Object.values(UserRole) as string[]).includes(value);

export const RBACProvider: React.FC<RBACProviderProps> = ({ children }) => {
  const [claims, setClaims] = useState<UserClaims | null>(null);
  const [userProfile, setUserProfile] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Matrice de référence (lecture seule) tant que le backend n'expose pas les permissions du compte.
  const roleDefinitions: RoleDefinition[] = DEFAULT_ROLE_DEFINITIONS;

  /**
   * Load user claims from Firebase Auth token
   */
  const loadClaims = useCallback(async () => {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      setClaims(null);
      setUserProfile(null);
      setLoading(false);
      return;
    }

    try {
      // Force refresh to get latest custom claims
      const idTokenResult = await currentUser.getIdTokenResult(true);

      // Compte sans rôle back-office (ex. élève de l'app mobile) : aucun accès.
      // Plus de rôle « lecteur » attribué par défaut (audit console S-5).
      const role = idTokenResult.claims.role;
      if (!isUserRole(role)) {
        logger.warn('[RBACContext][loadClaims] DECISION refus reason=aucun rôle back-office');
        setClaims(null);
        setUserProfile(null);
        setError(NO_ROLE_ERROR);
        return;
      }

      const userClaims: UserClaims = {
        uid: currentUser.uid,
        email: currentUser.email || '',
        role,
        classes: (idTokenResult.claims.classes as string[]) || []
      };

      setClaims(userClaims);

      // Load full user profile from API (includes canUseAICorrection, isReadOnly, etc.)
      try {
        const profileResponse = await AdminUsersApiService.getMyProfile();
        if (profileResponse.success && profileResponse.data) {
          setUserProfile(profileResponse.data as AdminUser);
        }
      } catch (profileErr) {
        logger.warn('Could not load user profile:', profileErr);
        // Don't fail completely if profile can't be loaded
      }

      setError(null);
    } catch (err) {
      logger.error('Error loading claims:', err);
      setError('Failed to load user permissions');
      setClaims(null);
      setUserProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Refresh custom claims from server
   */
  const refreshClaims = useCallback(async () => {
    setLoading(true);
    await loadClaims();
  }, [loadClaims]);

  /**
   * Setup auth state listener
   */
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user: User | null) => {
      if (user) {
        loadClaims();
      } else {
        setClaims(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [loadClaims]);

  // ========================================================================
  // ROLE CHECKS
  // ========================================================================

  /**
   * Check if user has a specific role or any of multiple roles
   */
  const hasRole = useCallback(
    (role: UserRole | UserRole[]): boolean => {
      if (!claims) return false;

      if (Array.isArray(role)) {
        return role.includes(claims.role);
      }

      return claims.role === role;
    },
    [claims]
  );

  /**
   * Check if user is an admin (admin or super_admin)
   */
  const isAdminUser = useCallback((): boolean => {
    if (!claims) return false;
    return isAdmin(claims.role);
  }, [claims]);

  /**
   * Check if user is a super admin
   */
  const isSuperAdminUser = useCallback((): boolean => {
    if (!claims) return false;
    return isSuperAdmin(claims.role);
  }, [claims]);

  /**
   * Check if current user can manage a target role
   */
  const canManageRoleCheck = useCallback(
    (targetRole: UserRole): boolean => {
      if (!claims) return false;
      return canManageRoleUtil(claims.role, targetRole);
    },
    [claims]
  );

  // ========================================================================
  // PERMISSION CHECKS
  // ========================================================================

  /**
   * Get role definition for current user
   */
  const getCurrentRoleDefinition = useCallback((): RoleDefinition | undefined => {
    if (!claims) return undefined;
    return roleDefinitions.find((def) => def.id === claims.role);
  }, [claims, roleDefinitions]);

  /**
   * Check if user has permission to perform action on resource
   */
  const hasPermission = useCallback(
    (resource: string, action: string | ResourceAction): boolean => {
      if (!claims) return false;

      // Super admin has all permissions
      if (claims.role === UserRole.SUPER_ADMIN) {
        return true;
      }

      // Get role definition
      const roleDef = getCurrentRoleDefinition();
      if (!roleDef) return false;

      // Check permissions
      for (const permission of roleDef.permissions) {
        // Check if resource matches
        const resourceMatches =
          permission.resource === '*' || permission.resource === resource;

        // Check if action matches
        const actionMatches =
          permission.actions.includes(ResourceAction.ALL) ||
          permission.actions.includes(action as ResourceAction);

        if (resourceMatches && actionMatches) {
          return true;
        }
      }

      return false;
    },
    [claims, getCurrentRoleDefinition]
  );

  /**
   * Check if user can access a specific class
   */
  const canAccessClass = useCallback(
    (classId: string): boolean => {
      if (!claims) return false;

      // Admin and Super Admin have access to all classes
      if (isAdmin(claims.role)) {
        return true;
      }

      // Lecteur only has access to assigned classes
      return claims.classes?.includes(classId) || false;
    },
    [claims]
  );

  /**
   * Check if user can use AI-assisted correction features
   * Requires canUseAICorrection flag in user profile (set by super_admin)
   */
  const canUseAICorrectionCheck = useCallback((): boolean => {
    if (!userProfile) return false;
    return userProfile.canUseAICorrection === true;
  }, [userProfile]);

  /**
   * Get list of accessible class IDs
   * Returns empty array for admins (meaning all classes)
   */
  const getAccessibleClassIds = useCallback((): string[] => {
    if (!claims) return [];

    // Admin and Super Admin have access to all classes
    if (isAdmin(claims.role)) {
      return []; // Empty array means "all classes"
    }

    // Return assigned classes
    return claims.classes || [];
  }, [claims]);

  /**
   * Get role definitions (for UI)
   */
  const getRoleDefinitions = useCallback((): RoleDefinition[] => {
    return roleDefinitions;
  }, [roleDefinitions]);

  // ========================================================================
  // CONTEXT VALUE
  // ========================================================================

  const value: RBACContextType = {
    claims,
    userProfile,
    loading,
    error,
    hasRole,
    isAdmin: isAdminUser,
    isSuperAdmin: isSuperAdminUser,
    canManageRole: canManageRoleCheck,
    hasPermission,
    canAccessClass,
    canUseAICorrection: canUseAICorrectionCheck,
    getAccessibleClassIds,
    refreshClaims,
    getRoleDefinitions
  };

  return <RBACContext.Provider value={value}>{children}</RBACContext.Provider>;
};

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to access RBAC context
 *
 * @throws Error if used outside RBACProvider
 */
export const useRBAC = (): RBACContextType => {
  const context = useContext(RBACContext);

  if (context === undefined) {
    throw new Error('useRBAC must be used within an RBACProvider');
  }

  return context;
};

// ============================================================================
// UTILITY HOOKS
// ============================================================================

// ============================================================================
// EXPORTS
// ============================================================================

export default RBACContext;
